/**
 * SAMBA configuration API types
 */

import {
    type IpgAllowedWebsite,
    type IpwAllowedWebsite,
    MerchantConfigSchema,
    type MerchantConfig,
    type MerchantConfigStorage,
    type PortalAllowedWebsite,
    RoleAssignmentCustomConfigSchema,
    type RoleAssignmentCustomConfig,
    type RpaBuilderWebsiteConfig,
    type UnmanagedAllowedWebsite,
    type UserRoleAssignmentConfigPatch,
} from './backend_config.types';
import type { InternalRole } from './identity-contracts';
import type { ReleaseIdentityV1 } from './native-release-catalog.types';

export type SambaInternalRole = InternalRole;

export interface SambaAutoupdateConfigV1 {
    sambaInstallerVersion: string;
    forceAutoupdate: boolean;
}

export type SambaAutoupdateConfig =
    | { updatesEnabled: false }
    | {
          updatesEnabled: true;
          nativeIdentity: ReleaseIdentityV1;
          forceAutoupdate: boolean;
      };

export interface EffectiveConfigResponse extends MerchantConfig {
    sambaAutoupdate: SambaAutoupdateConfig;
    internalRole?: SambaInternalRole;
}

export interface EffectiveConfigResponseV1 extends MerchantConfig {
    sambaAutoupdate: SambaAutoupdateConfigV1;
    internalRole?: SambaInternalRole;
}

export const NavigateMenuTargetKind = {
    INTEGRATED_WEBSITE: 'integrated_website' as const,
    UNMANAGED_ALLOWED_WEBSITE: 'unmanaged_allowed_website' as const,
    RPA_BUILDER_WEBSITE: 'rpa_builder_website' as const,
} as const;

export type NavigateMenuTargetKind =
    (typeof NavigateMenuTargetKind)[keyof typeof NavigateMenuTargetKind];

export type NavigateMenuIntegratedWebsite = (
    | IpgAllowedWebsite
    | IpwAllowedWebsite
    | PortalAllowedWebsite
) & {
    readonly navigate_target_kind: typeof NavigateMenuTargetKind.INTEGRATED_WEBSITE;
};

export type NavigateMenuUnmanagedAllowedWebsite = UnmanagedAllowedWebsite & {
    readonly navigate_target_kind: typeof NavigateMenuTargetKind.UNMANAGED_ALLOWED_WEBSITE;
};

export type NavigateMenuRpaBuilderWebsite = UnmanagedAllowedWebsite & {
    readonly navigate_target_kind: typeof NavigateMenuTargetKind.RPA_BUILDER_WEBSITE;
    readonly organization_uuid: string;
    readonly organization_allowed_website_uuid: string;
    readonly allowed_website_template_uuid?: string;
    readonly rbs_config: RpaBuilderWebsiteConfig;
};

export type NavigateMenuWebsite =
    | NavigateMenuIntegratedWebsite
    | NavigateMenuUnmanagedAllowedWebsite
    | NavigateMenuRpaBuilderWebsite;

export interface NavigateMenuWebsitesResponse {
    websites: NavigateMenuWebsite[];
}

/**
 * Update config request
 */
export interface UpdateConfigRequest {
    config: MerchantConfigStorage;
}

export interface UpdateRoleAssignmentConfigRequest {
    expected_config: RoleAssignmentCustomConfig;
    config: RoleAssignmentCustomConfig;
}

export interface PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest {
    userRoleAssignmentConfig: UserRoleAssignmentConfigPatch;
}

/**
 * Combined config response
 */
export interface CombinedConfigResponse {
    user_config: RoleAssignmentCustomConfig;
    org_config: MerchantConfig;
}

/**
 * Role assignment config response
 * Used when getting/updating config for a specific role assignment
 */
export interface RoleAssignmentConfigResponse {
    role_assignment_id: number;
    user_config: RoleAssignmentCustomConfig;
}

/**
 * Get effective config response
 */
export interface GetEffectiveConfigResponse {
    config: MerchantConfig;
    version: string;
}

export interface CloverRuntimeEnvironmentAvailabilityResponse {
    readonly environments: {
        readonly SANDBOX: boolean;
        readonly PRODUCTION: boolean;
    };
}

/**
 * Merge org_config and user_config
 * User config takes precedence over org config
 */
export function mergeConfigs(
    orgConfig?: MerchantConfig,
    userConfig?: RoleAssignmentCustomConfig
): MerchantConfig {
    const organizationConfig: MerchantConfig = MerchantConfigSchema.parse(orgConfig ?? {});
    const roleAssignmentConfig: RoleAssignmentCustomConfig = RoleAssignmentCustomConfigSchema.parse(
        userConfig ?? {}
    );
    const mergedConfigValue: MergeableConfigRecord = deepMergeConfigObjects(
        organizationConfig,
        roleAssignmentConfig
    );
    return MerchantConfigSchema.parse(mergedConfigValue);
}

type MergeableConfigPrimitive = boolean | number | string | null | undefined;

type MergeableConfigObject = object;

type MergeableConfigValue =
    | MergeableConfigPrimitive
    | MergeableConfigObject
    | ReadonlyArray<MergeableConfigValue>;

interface MergeableConfigRecord {
    [key: string]: MergeableConfigValue;
}

function isMergeableObject(value: MergeableConfigValue): value is MergeableConfigObject {
    return (
        value !== null &&
        value !== undefined &&
        !Array.isArray(value) &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
}

function toMergeableConfigRecord(value: MergeableConfigObject): MergeableConfigRecord {
    // Safe bridge: this helper is only called for MerchantConfig and
    // RoleAssignmentCustomConfig plain-object branches validated by
    // Zod and recursively composed from the same config domain.
    // eslint-disable-next-line no-restricted-syntax
    return value as MergeableConfigRecord;
}

function deepMergeConfigObjects(
    baseObject: MergeableConfigObject,
    overrideObject: MergeableConfigObject
): MergeableConfigRecord {
    const baseRecord: MergeableConfigRecord = toMergeableConfigRecord(baseObject);
    const overrideRecord: MergeableConfigRecord = toMergeableConfigRecord(overrideObject);
    const mergedRecord: MergeableConfigRecord = { ...baseRecord };

    for (const overrideKey of Object.keys(overrideRecord)) {
        const overrideEntryValue: MergeableConfigValue = overrideRecord[overrideKey];
        const mergedEntryValue: MergeableConfigValue = deepMergeConfigValues(
            baseRecord[overrideKey],
            overrideEntryValue
        );

        if (mergedEntryValue !== undefined) {
            mergedRecord[overrideKey] = mergedEntryValue;
        }
    }

    return mergedRecord;
}

function deepMergeConfigValues(
    baseValue: MergeableConfigValue,
    overrideValue: MergeableConfigValue
): MergeableConfigValue {
    if (overrideValue === undefined) {
        return baseValue;
    }

    if (Array.isArray(baseValue) && Array.isArray(overrideValue)) {
        return [...baseValue, ...overrideValue];
    }

    if (Array.isArray(overrideValue)) {
        return [...overrideValue];
    }

    if (isMergeableObject(baseValue) && isMergeableObject(overrideValue)) {
        return deepMergeConfigObjects(baseValue, overrideValue);
    }

    return overrideValue;
}
